'use client'

import { type PolymorphicProps, useSlotProps } from '@mui/base'
import { clsx } from 'clsx'
import { type ComponentProps, type ElementType, type JSX } from 'react'

type LinkOwnProps<RootComponentType extends ElementType = 'a'> = {
  /**
   * @default brand
   */
  color?: 'brand' | 'inherit'

  /**
   * If `true`, the `::after` pseudo-element is added to cover the area of interaction.
   * The parent of the overlay `Link` should have `relative` CSS position.
   */
  overlay?: boolean

  ref?: ComponentProps<RootComponentType>['ref']

  slotProps?: {
    root?: ComponentProps<RootComponentType>
  }

  slots?: {
    root?: RootComponentType
  }
}

interface LinkTypeMap<RootComponentType extends ElementType = 'a'> {
  defaultComponent: RootComponentType
  props: LinkOwnProps<RootComponentType>
}

export type LinkProps<
  RootComponentType extends ElementType = LinkTypeMap['defaultComponent'],
> = PolymorphicProps<LinkTypeMap<RootComponentType>, RootComponentType>

/**
 * The Link component allows you to easily customize anchor elements with theme colors
 */
export const Link = <RootComponentType extends ElementType = 'a'>(
  props: LinkProps<RootComponentType>,
): JSX.Element => {
  const {
    color = 'brand',
    overlay,
    ref,
    slotProps,
    slots,
    ...restProps
  } = props

  const Root = slots?.root ?? 'a'
  const rootProps = useSlotProps({
    additionalProps: {
      /**
       * Explicitly pass `ref` to `additionalProps` so `useSlotProps` resolves it,
       * if it's in `restProps`, `rootProps.ref` will be resolved to `null`.
       */
      ref,
    },
    className: clsx(
      'link-deprecated',
      color === 'brand' && 'link-deprecated_brand',
      overlay && 'link-deprecated_overlay',
    ),
    elementType: Root,
    externalForwardedProps: restProps,
    externalSlotProps: slotProps?.root,
    ownerState: undefined,
  })

  return <Root {...rootProps} />
}
